import type posthogJs from 'posthog-js';

type PostHogInstance = typeof posthogJs;

const POSTHOG_KEY = 'phc_5q80eFjB4EBpHqwQ5WMJb19g4qEvD6ERNXTpxEKEbnS';
const POSTHOG_HOST = 'https://us.i.posthog.com';

let isInitialized = false;
let instance: PostHogInstance | null = null;
let loadPromise: Promise<PostHogInstance | null> | null = null;
const queue: Array<(ph: PostHogInstance) => void> = [];

type InitPostHogOptions = {
  lightweight?: boolean;
};

function flushQueue(ph: PostHogInstance) {
  while (queue.length) {
    const job = queue.shift();
    try {
      job?.(ph);
    } catch {
      // no-op: analytics nunca deve quebrar a aplicação
    }
  }
}

function loadPostHog({ lightweight = false }: InitPostHogOptions = {}): Promise<PostHogInstance | null> {
  if (loadPromise) return loadPromise;

  loadPromise = import('posthog-js')
    .then(({ default: ph }) => {
      ph.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,

        // Capture options
        autocapture: !lightweight,
        capture_pageview: true,
        capture_pageleave: !lightweight,

        // Web Vitals nativo (LCP, FID, CLS, etc.)
        capture_performance: !lightweight,

        // Session recording (substitui Sentry Replay)
        disable_session_recording: lightweight,
        disable_surveys: lightweight,
        disable_external_dependency_loading: lightweight,
        advanced_disable_flags: lightweight,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-ph-mask]',
        },

        // Privacy
        respect_dnt: true,
      });

      instance = ph;
      flushQueue(ph);
      return ph;
    })
    .catch(() => {
      queue.length = 0;
      return null;
    });

  return loadPromise;
}

function withPostHog(fn: (ph: PostHogInstance) => void) {
  if (!import.meta.env.PROD) return;
  if (instance) {
    try {
      fn(instance);
    } catch {
      // no-op
    }
    return;
  }
  queue.push(fn);
  if (isInitialized) loadPostHog();
}

/**
 * Marca o PostHog como habilitado e carrega o SDK de forma assíncrona.
 * Pode ser chamado após o load/idle — eventos disparados antes ficam na fila.
 */
export function initPostHog(options: InitPostHogOptions = {}) {
  if (isInitialized || typeof window === 'undefined') return;

  // Only init in production
  if (import.meta.env.PROD) {
    isInitialized = true;
    loadPostHog(options);
  }
}

export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  withPostHog((ph) => ph.identify(userId, properties));
}

export function resetUser() {
  withPostHog((ph) => ph.reset());
}

export function captureEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!import.meta.env.PROD) {
    console.log(`[PostHog] Event: ${eventName}`, properties);
    return;
  }

  withPostHog((ph) => ph.capture(eventName, properties));
}

export function captureException(error: Error, context?: Record<string, unknown>) {
  if (!import.meta.env.PROD) {
    console.error(`[PostHog] Exception:`, error, context);
    return;
  }

  // SDK monta $exception_list no formato Cymbal automaticamente
  withPostHog((ph) => ph.captureException(error, context));
}

export function setPersonProperties(properties: Record<string, unknown>) {
  withPostHog((ph) => ph.setPersonProperties(properties));
}


// ========== EVENTOS DE NEGÓCIO ==========

/**
 * Dispara quando uma análise radiestésica é concluída
 */
export function trackAnalysisCreated(data: {
  sessionId: string;
  targetName: string;
  targetType: string;
  planName: string;
}) {
  captureEvent('analysis_created', {
    session_id: data.sessionId,
    target_name: data.targetName,
    target_type: data.targetType,
    plan_name: data.planName,
  });
}

/**
 * Dispara quando um relatório é gerado pela IA
 */
export function trackReportGenerated(data: {
  sessionId: string;
  targetName: string;
  fromCache: boolean;
  generationTimeMs?: number;
}) {
  captureEvent('report_generated', {
    session_id: data.sessionId,
    target_name: data.targetName,
    from_cache: data.fromCache,
    generation_time_ms: data.generationTimeMs,
  });
}

/**
 * Dispara quando o usuário faz upgrade de plano
 */
export function trackPlanUpgraded(data: {
  oldPlan: string;
  newPlan: string;
  value?: number;
  isInstant: boolean;
  transactionId?: string;
}) {
  captureEvent('plan_upgraded', {
    old_plan: data.oldPlan,
    new_plan: data.newPlan,
    value: data.value,
    is_instant: data.isInstant,
    transaction_id: data.transactionId,
    currency: 'BRL',
  });
}

/**
 * Dispara quando o checkout é iniciado
 */
export function trackCheckoutInitiated(data: {
  planName: string;
  value: number;
  currentPlan: string;
  priceId?: string;
}) {
  captureEvent('checkout_initiated', {
    plan_name: data.planName,
    value: data.value,
    current_plan: data.currentPlan,
    price_id: data.priceId,
    currency: 'BRL',
  });
}

/**
 * Dispara quando o checkout é abandonado
 */
export function trackCheckoutAbandoned(data: {
  planName?: string;
  currentPlan: string;
  value?: number;
  abandonmentType: 'explicit_cancel' | 'timeout' | 'page_unload';
  elapsedMinutes?: number;
}) {
  captureEvent('checkout_abandoned', {
    attempted_plan: data.planName || 'unknown',
    current_plan: data.currentPlan,
    value: data.value,
    abandonment_type: data.abandonmentType,
    elapsed_minutes: data.elapsedMinutes,
    currency: data.value ? 'BRL' : undefined,
  });
}

/**
 * Dispara quando um novo usuário se cadastra (lead)
 */
/**
 * Dispara quando um trial é iniciado
 */
export function trackTrialStarted(data: {
  userId: string;
  planName: string;
  trialDays: number;
}) {
  captureEvent('trial_started', {
    user_id: data.userId,
    plan_name: data.planName,
    trial_days: data.trialDays,
  });
}

export function trackLead(data: {
  userId: string;
  email: string;
  signupMethod: 'email' | 'google';
  planContext?: string;
}) {
  captureEvent('lead', {
    user_id: data.userId,
    signup_method: data.signupMethod,
    plan_context: data.planContext,
    // Não logar email por privacidade, mas podemos identificar o user
  });
  
  // Identificar o usuário no PostHog
  identifyUser(data.userId, {
    email: data.email,
    signup_method: data.signupMethod,
    plan_context: data.planContext,
  });
}

/**
 * Dispara quando um usuário faz login (sessão de retorno)
 */
export function trackLogin(data: {
  userId: string;
  loginMethod: 'email' | 'google' | 'token_refresh';
  isReturningUser?: boolean;
}) {
  captureEvent('login', {
    user_id: data.userId,
    login_method: data.loginMethod,
    is_returning_user: data.isReturningUser,
  });
}

/**
 * Dispara quando um usuário faz logout
 */
export function trackLogout(data: {
  userId: string;
  sessionDurationMinutes?: number;
}) {
  captureEvent('logout', {
    user_id: data.userId,
    session_duration_minutes: data.sessionDurationMinutes,
  });
}

/**
 * Dispara quando uma análise é compartilhada
 */
export function trackAnalysisShared(data: {
  sessionId: string;
  shareMethod: string;
  targetName: string;
}) {
  captureEvent('analysis_shared', {
    session_id: data.sessionId,
    share_method: data.shareMethod,
    target_name: data.targetName,
  });
}

/**
 * Dispara quando uma compra é finalizada
 */
export function trackPurchase(data: {
  transactionId: string;
  value: number;
  planName: string;
}) {
  captureEvent('purchase', {
    transaction_id: data.transactionId,
    value: data.value,
    plan_name: data.planName,
    currency: 'BRL',
  });
}

export function getPostHog(): PostHogInstance | null {
  return instance;
}
